import { useState } from "react";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

interface FormData {
  nombre: string;
  correo: string;
  contrasena: string;
  telefono: string;
  terminos: boolean;
}

interface Step1Props {
  data: FormData;
  onChange: (data: FormData) => void;
  onNext: () => void;
}

export default function Step1Titular({ data, onChange, onNext }: Step1Props) {
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData & { contrasena: string }>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!data.nombre.trim()) e.nombre = "El nombre es requerido";
    if (!data.correo.includes("@")) e.correo = "Correo inválido";
    if (data.contrasena.length < 8) e.contrasena = "Mínimo 8 caracteres";
    if (!data.telefono.trim()) e.telefono = "El teléfono es requerido";
    if (!data.terminos) e.terminos = "Debes aceptar los términos";
    setErrors(e as Partial<FormData>);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Datos del titular</h2>
        <p className="text-sm text-slate-500">El responsable legal de la cuenta emite.do.</p>
      </div>

      {/* Nombre */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Nombre completo</label>
        <input
          type="text"
          value={data.nombre}
          onChange={(e) => onChange({ ...data, nombre: e.target.value })}
          placeholder="Juan Pérez García"
          className={`w-full border rounded-xl px-4 py-2.5 text-sm outline-none transition-colors ${
            errors.nombre ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-700"
          }`}
        />
        {errors.nombre && <p className="text-xs text-red-500 mt-1">{errors.nombre}</p>}
      </div>

      {/* Correo */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Correo electrónico</label>
        <input
          type="email"
          value={data.correo}
          onChange={(e) => onChange({ ...data, correo: e.target.value })}
          placeholder="juan@empresa.com.do"
          className={`w-full border rounded-xl px-4 py-2.5 text-sm outline-none transition-colors ${
            errors.correo ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-700"
          }`}
        />
        {errors.correo && <p className="text-xs text-red-500 mt-1">{errors.correo}</p>}
      </div>

      {/* Contraseña */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Contraseña</label>
        <div className="relative">
          <input
            type={showPw ? "text" : "password"}
            value={data.contrasena}
            onChange={(e) => onChange({ ...data, contrasena: e.target.value })}
            placeholder="Mínimo 8 caracteres"
            className={`w-full border rounded-xl px-4 py-2.5 pr-10 text-sm outline-none transition-colors ${
              errors.contrasena ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-700"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPw(!showPw)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.contrasena && <p className="text-xs text-red-500 mt-1">{errors.contrasena}</p>}
        {data.contrasena && (
          <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                data.contrasena.length < 8 ? "w-1/4 bg-red-400" :
                data.contrasena.length < 12 ? "w-2/4 bg-amber-400" :
                "w-full bg-emerald-500"
              }`}
            />
          </div>
        )}
      </div>

      {/* Teléfono */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Teléfono</label>
        <input
          type="tel"
          value={data.telefono}
          onChange={(e) => onChange({ ...data, telefono: e.target.value })}
          placeholder="+1 (809) 555-0001"
          className={`w-full border rounded-xl px-4 py-2.5 text-sm outline-none transition-colors ${
            errors.telefono ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-700"
          }`}
        />
        {errors.telefono && <p className="text-xs text-red-500 mt-1">{errors.telefono}</p>}
      </div>

      {/* Términos */}
      <div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={data.terminos}
            onChange={(e) => onChange({ ...data, terminos: e.target.checked })}
            className="mt-0.5 accent-blue-800 w-4 h-4"
          />
          <span className="text-sm text-slate-600">
            Acepto los{" "}
            <Link to="/terms" className="text-blue-800 hover:underline cursor-pointer">Términos de Servicio</Link>
            {" "}y la{" "}
            <Link to="/privacy" className="text-blue-800 hover:underline cursor-pointer">Política de Privacidad</Link>
          </span>
        </label>
        {errors.terminos && <p className="text-xs text-red-500 mt-1">{errors.terminos}</p>}
      </div>

      <button
        onClick={handleNext}
        className="w-full flex items-center justify-center gap-2 bg-blue-800 text-white font-semibold py-3 rounded-xl hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
      >
        Continuar
        <ArrowRight size={16} />
      </button>
    </div>
  );
}